import { useEffect, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";

import { BOOTSTRAP_KEY } from "../api/queryKeys";
import { endSession, renewSession } from "../api/session";
import { classifyProblem } from "../domain/problem";
import { setPendingPath } from "./pendingPath";

export interface ForbiddenNotice {
  requiredPermission: string | null;
  detail: string | null;
}

/**
 * A second 401 inside this window is not an expired token -- a token MSAL has just renewed is
 * being refused (a misconfigured audience, a principal the API will not accept) -- so it signs
 * out rather than renewing in a loop.
 */
export const RENEWAL_WINDOW_MS = 60_000;

let lastRenewalAt = 0;

/** Tests only: forget the last renewal so each case starts outside the window. */
export function resetRenewalWindow(): void {
  lastRenewalAt = 0;
}

/**
 * The 401 and the 403 in anger.
 *
 *   * **401** — the token was refused. One forced silent renewal through the seam
 *     (`renewSession`); when it works, the failed reads are refetched. When MSAL needs the person
 *     back the page is already redirecting. When there is no account to renew, or a renewed token
 *     is refused again, the session is ended and the person signs in again from `/login`,
 *     returning here afterwards.
 *   * **403 naming a permission** surfaces inline (ProblemAlert is Phase 8).
 *   * **403 naming none** is the tenant refusing this principal outright — no membership, or the
 *     membership was removed mid-session (AU-D5). That is the bootstrap's question, not this
 *     page's, so the bootstrap is refetched and the shell shows its full-page problem.
 */
export function useApiErrorWatch(): { forbidden: ForbiddenNotice | null; dismiss: () => void } {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [forbidden, setForbidden] = useState<ForbiddenNotice | null>(null);

  useEffect(() => {
    let renewing = false;

    const toSignIn = async (path: string) => {
      setPendingPath(path);
      await endSession();
      void navigate({ to: "/login", replace: true });
    };

    const unauthenticated = async (path: string) => {
      if (renewing) return;
      renewing = true;
      try {
        if (Date.now() - lastRenewalAt < RENEWAL_WINDOW_MS) {
          await toSignIn(path);
          return;
        }
        lastRenewalAt = Date.now();
        const outcome = await renewSession(path);
        if (outcome === "renewed") {
          void queryClient.refetchQueries({ type: "active", predicate: (q) => !!q.state.error });
        } else if (outcome === "signed-out") {
          await toSignIn(path);
        }
        // "redirecting": the page is on its way to the identity provider.
      } finally {
        renewing = false;
      }
    };

    const handle = (error: unknown, path: string, fromBootstrap = false) => {
      const classified = classifyProblem(error);
      if (classified.kind === "unauthenticated") {
        void unauthenticated(path);
        return;
      }
      if (classified.kind === "forbidden") {
        if (!classified.requiredPermission) {
          // The bootstrap's own refusal is already on its way to the full-page problem.
          if (!fromBootstrap) void queryClient.invalidateQueries({ queryKey: BOOTSTRAP_KEY });
          return;
        }
        setForbidden({
          requiredPermission: classified.requiredPermission,
          detail: classified.problem?.detail ?? null,
        });
      }
    };

    const here = () => {
      if (typeof window === "undefined") return "/";
      const { pathname, search, hash } = window.location;
      return `${pathname}${search}${hash}`;
    };

    // Only the failure itself, never a later event that still carries it: a refetch starting
    // after a renewal keeps the old 401 in `state.error` until it lands, and reading that as a
    // second refusal would sign out a session that had just been renewed.
    const unsubQueries = queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== "updated" || event.action.type !== "error") return;
      const error = event.query.state.error;
      if (!error) return;
      const key = event.query.queryKey;
      const fromBootstrap =
        key.length === BOOTSTRAP_KEY.length &&
        key.every((k: unknown, i: number) => k === BOOTSTRAP_KEY[i]);
      handle(error, here(), fromBootstrap);
    });
    const unsubMutations = queryClient.getMutationCache().subscribe((event) => {
      if (event.type !== "updated" || event.action.type !== "error") return;
      const error = event.mutation?.state.error;
      if (error) handle(error, here());
    });

    return () => {
      unsubQueries();
      unsubMutations();
    };
  }, [queryClient, navigate]);

  return { forbidden, dismiss: () => setForbidden(null) };
}
